import Seo from '@/components/Seo';
import SiteHeader from '@/components/site/SiteHeader';
import SiteFooter from '@/components/site/SiteFooter';
import Hover from '@/components/site/Hover';
import Field from '@/components/site/Field';
import Turnstile from '@/components/Turnstile';
import { css } from '@/components/site/css';
import { useContactForm } from '@/components/site/useContactForm';

const inputStyle =
  'width:100%;font-family:inherit;font-size:15px;color:#fff;background:#24276C;border:1px solid #3A3D82;border-radius:10px;padding:13px 14px';
const inputFocus = 'border-color:#E9402B;outline:none';
const labelStyle = 'display:block;font-size:13px;font-weight:600;color:#B9C0DC;margin-bottom:7px';

/** Restaurant operators (pilot / early access) page. Ported verbatim from
 *  Operator.dc.html. */
export default function OperatorPage() {
  const { submitted, submitting, error, setToken, resetSignal, turnstileEnabled, onSubmit } =
    useContactForm('operator');

  return (
    <div
      className="site-shell"
      style={css(
        "font-family:'Hanken Grotesk',system-ui,sans-serif;color:#24276C;background:#ffffff;-webkit-font-smoothing:antialiased;overflow-x:clip"
      )}
    >
      <Seo
        title="F&B Insights — Menu pricing benchmarks for restaurant operators"
        description="Stop pricing your menu by guesswork. We benchmark your pricing against your real local competitive set, on real dine-in data. Join the pilot."
        path="/operator"
      />

      <SiteHeader
        navLinks={[
          { label: 'What you get now', href: '#now' },
          { label: "Where it's going", href: '#roadmap' },
        ]}
        cta={{ label: 'Join the pilot', href: '#pilot', bg: '#E9402B', hoverBg: '#D3341F' }}
      />

      <span id="top" />

      {/* Hero */}
      <section style={css('background:#24276C')}>
        <div
          className="site-hero-grid"
          style={css(
            'max-width:1180px;margin:0 auto;padding:80px 28px 72px;display:grid;grid-template-columns:minmax(0,1.05fr) minmax(0,0.95fr);gap:60px;align-items:center'
          )}
        >
          <div>
            <div
              style={css(
                'display:inline-flex;align-items:center;gap:9px;font-size:13px;font-weight:600;letter-spacing:0.04em;text-transform:uppercase;color:#F0806F;margin-bottom:22px'
              )}
            >
              <span
                style={css(
                  'width:7px;height:7px;border-radius:50%;background:#E9402B;display:inline-block'
                )}
              ></span>
              Early access · for multi-unit operators
            </div>
            <h1
              style={css(
                "font-family:'Schibsted Grotesk',sans-serif;font-size:clamp(38px,4.8vw,58px);line-height:1.05;font-weight:800;letter-spacing:-0.02em;margin:0 0 24px;color:#ffffff;text-wrap:balance"
              )}
            >
              Stop pricing your menu by guesswork.
            </h1>
            <p
              style={css(
                'font-size:20px;line-height:1.5;color:#B9C0DC;margin:0 0 34px;max-width:520px;text-wrap:pretty'
              )}
            >
              You leave real margin on the table because the only way to check the market is sending
              managers out to visit competitors. We do that recon for you, on real dine-in prices,
              so you can stay competitively priced without the field trips.
            </p>
            <div className="site-hero-cta" style={css('display:flex;flex-wrap:wrap;gap:14px')}>
              <Hover
                href="#pilot"
                style={css(
                  'display:inline-flex;align-items:center;gap:9px;font-size:16px;font-weight:600;color:#fff;background:#E9402B;padding:15px 26px;border-radius:10px'
                )}
                hoverStyle={css('background:#D3341F')}
              >
                Get your menu benchmarked<span style={css('font-size:18px;line-height:1')}>→</span>
              </Hover>
              <Hover
                href="#now"
                style={css(
                  'display:inline-flex;align-items:center;font-size:16px;font-weight:600;color:#fff;background:transparent;border:1.5px solid rgba(255,255,255,0.35);padding:15px 26px;border-radius:10px'
                )}
                hoverStyle={css('border-color:#ffffff')}
              >
                See what you get
              </Hover>
            </div>
          </div>

          <div style={css('position:relative')}>
            <div
              style={css(
                'position:absolute;inset:-18px auto auto -18px;width:76px;height:76px;border-radius:16px;background:#E9402B;opacity:0.1'
              )}
            ></div>
            <div
              style={css(
                'position:relative;background:#fff;border:1px solid #E6E4DE;border-radius:18px;box-shadow:0 24px 60px -28px rgba(36,39,108,0.32);padding:26px'
              )}
            >
              <div
                style={css(
                  'display:flex;align-items:center;justify-content:space-between;margin-bottom:18px'
                )}
              >
                <span
                  style={css(
                    "font-family:'Schibsted Grotesk',sans-serif;font-size:15px;font-weight:700;color:#24276C"
                  )}
                >
                  Your menu vs. local market
                </span>
                <span
                  style={css(
                    'font-size:12px;font-weight:600;color:#54545E;background:#EFEEEA;padding:5px 10px;border-radius:20px'
                  )}
                >
                  3-mile radius
                </span>
              </div>
              <div style={css('display:flex;flex-direction:column;gap:16px')}>
                {[
                  ['Burgers', 'at market', '#12A150', '52%', '20%', '24%'],
                  ['Cocktails', 'below market', '#E9402B', '30%', '22%', '20%'],
                  ['Wine by the glass', 'at market', '#12A150', '58%', '30%', '18%'],
                  ['Appetizers', 'above market', '#C58A00', '78%', '26%', '22%'],
                ].map(([label, tag, color, left, barLeft, barRight]) => (
                  <div key={label}>
                    <div
                      style={css(
                        'display:flex;justify-content:space-between;align-items:baseline;margin-bottom:7px'
                      )}
                    >
                      <span style={css('font-size:14px;color:#43434D')}>{label}</span>
                      <span style={css(`font-size:13px;font-weight:600;color:${color}`)}>
                        {tag}
                      </span>
                    </div>
                    <div
                      style={css(
                        'position:relative;height:8px;border-radius:6px;background:#F0EFEB'
                      )}
                    >
                      <div
                        style={css(
                          `position:absolute;left:${left};top:-3px;width:3px;height:14px;border-radius:2px;background:#24276C`
                        )}
                      ></div>
                      <div
                        style={css(
                          `position:absolute;left:${barLeft};right:${barRight};top:0;height:8px;border-radius:6px;background:#C9D0E6`
                        )}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
              <div
                style={css(
                  'display:flex;align-items:center;gap:16px;margin-top:20px;padding-top:16px;border-top:1px solid #F0EFEB;font-size:12px;color:#8A8A93'
                )}
              >
                <span style={css('display:inline-flex;align-items:center;gap:6px')}>
                  <span
                    style={css(
                      'width:3px;height:12px;border-radius:2px;background:#24276C;display:inline-block'
                    )}
                  ></span>
                  You
                </span>
                <span style={css('display:inline-flex;align-items:center;gap:6px')}>
                  <span
                    style={css(
                      'width:14px;height:8px;border-radius:6px;background:#C9D0E6;display:inline-block'
                    )}
                  ></span>
                  Local competitive set
                </span>
              </div>
            </div>
            <div
              style={css(
                'position:relative;text-align:center;margin-top:12px;font-size:12px;color:#94A0C4'
              )}
            >
              Illustrative view. Your benchmark is built from your own menu.
            </div>
          </div>
        </div>
      </section>

      {/* Problem */}
      <section
        style={css(
          'background:#F6F6F4;border-top:1px solid #ECEBE6;border-bottom:1px solid #ECEBE6'
        )}
      >
        <div style={css('max-width:1180px;margin:0 auto;padding:70px 28px')}>
          <div style={css('max-width:680px;margin-bottom:40px')}>
            <div
              style={css(
                'font-size:13px;font-weight:600;letter-spacing:0.04em;text-transform:uppercase;color:#E9402B;margin-bottom:14px'
              )}
            >
              The problem
            </div>
            <h2
              style={css(
                "font-family:'Schibsted Grotesk',sans-serif;font-size:clamp(28px,3.4vw,40px);line-height:1.1;font-weight:800;letter-spacing:-0.02em;margin:0 0 18px;color:#24276C"
              )}
            >
              Checking the market shouldn&rsquo;t cost you a week of manager time.
            </h2>
          </div>
          <div
            style={css(
              'display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:22px'
            )}
          >
            {[
              [
                'Manual and slow',
                'Managers drive to competitors, photograph menus, and type prices into a spreadsheet. It happens quarterly at best.',
              ],
              [
                'Incomplete',
                'You see a handful of nearby spots, not your real competitive set by type and price tier. The picture is always partial.',
              ],
              [
                'Margin left behind',
                "Priced too low, you give away margin. Priced too high, you lose covers. Without real data, you're guessing on both.",
              ],
            ].map(([title, body]) => (
              <div
                key={title}
                style={css(
                  'background:#fff;border:1px solid #E6E4DE;border-radius:14px;padding:26px 24px'
                )}
              >
                <div
                  style={css(
                    "font-family:'Schibsted Grotesk',sans-serif;font-size:17px;font-weight:700;color:#24276C;margin-bottom:8px"
                  )}
                >
                  {title}
                </div>
                <p style={css('font-size:15px;line-height:1.55;color:#54545E;margin:0')}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Now */}
      <section id="now" style={css('max-width:1180px;margin:0 auto;padding:80px 28px 28px')}>
        <div style={css('display:flex;align-items:center;gap:14px;margin-bottom:16px')}>
          <span
            style={css(
              'display:inline-flex;align-items:center;gap:8px;font-size:13px;font-weight:700;color:#12A150;background:#EAF7EF;padding:7px 14px;border-radius:20px'
            )}
          >
            <span style={css('width:7px;height:7px;border-radius:50%;background:#12A150')}></span>
            Available now
          </span>
        </div>
        <h2
          style={css(
            "font-family:'Schibsted Grotesk',sans-serif;font-size:clamp(30px,3.6vw,44px);line-height:1.08;font-weight:800;letter-spacing:-0.02em;margin:0 0 18px;color:#24276C;max-width:760px"
          )}
        >
          We do the recon you currently send your managers out to do.
        </h2>
        <p
          style={css(
            'font-size:19px;line-height:1.55;color:#54545E;margin:0 0 44px;max-width:640px'
          )}
        >
          Upload your menu. We benchmark your pricing against your real local competitive set, so
          you can see how you compare to the market that matters and act on it.
        </p>

        <div
          style={css(
            'display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:22px;margin-bottom:34px'
          )}
        >
          {[
            [
              '1',
              'Send us your menu',
              "Share a menu URL or upload a file, plus the five competitors you always benchmark against. That's the whole setup on your side.",
            ],
            [
              '2',
              'We build your competitive set',
              'Restaurants like yours, by type and price tier, within a radius you choose. Not a random list of neighbors.',
            ],
            [
              '3',
              'You see where you stand',
              "A clear read on how your pricing compares to your real local market, so you know where you're leaving room.",
            ],
          ].map(([num, title, body]) => (
            <div
              key={num}
              style={css(
                'background:#fff;border:1px solid #E6E4DE;border-radius:16px;padding:32px 30px'
              )}
            >
              <div
                style={css(
                  "width:44px;height:44px;border-radius:11px;background:#24276C;color:#fff;display:flex;align-items:center;justify-content:center;font-family:'Schibsted Grotesk',sans-serif;font-weight:700;font-size:18px;margin-bottom:20px"
                )}
              >
                {num}
              </div>
              <h3
                style={css(
                  "font-family:'Schibsted Grotesk',sans-serif;font-size:20px;font-weight:700;margin:0 0 10px;color:#24276C"
                )}
              >
                {title}
              </h3>
              <p style={css('font-size:16px;line-height:1.55;color:#54545E;margin:0')}>{body}</p>
            </div>
          ))}
        </div>

        <div
          style={css(
            'display:flex;gap:16px;align-items:flex-start;background:#24276C;border-radius:16px;padding:26px 30px'
          )}
        >
          <span
            style={css(
              'flex:none;width:34px;height:34px;border-radius:9px;background:#E9402B;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:18px'
            )}
          >
            ✓
          </span>
          <p style={css('font-size:17px;line-height:1.55;color:#EAECF6;margin:0')}>
            <strong style={css('color:#fff')}>Built on real dine-in data.</strong> Every comparison
            uses actual on-premise menu prices, sourced and traceable. It&rsquo;s not delivery-app
            pricing marked up for commissions, which you&rsquo;d only have to discount to trust.
          </p>
        </div>
      </section>

      {/* Roadmap */}
      <section id="roadmap" style={css('max-width:1180px;margin:0 auto;padding:70px 28px 20px')}>
        <div style={css('display:flex;align-items:center;gap:14px;margin-bottom:16px')}>
          <span
            style={css(
              'display:inline-flex;align-items:center;gap:8px;font-size:13px;font-weight:700;color:#C58A00;background:#FBF3E2;padding:7px 14px;border-radius:20px'
            )}
          >
            Coming soon · on the roadmap
          </span>
        </div>
        <h2
          style={css(
            "font-family:'Schibsted Grotesk',sans-serif;font-size:clamp(30px,3.6vw,44px);line-height:1.08;font-weight:800;letter-spacing:-0.02em;margin:0 0 18px;color:#24276C;max-width:760px"
          )}
        >
          Where early-access members are headed next.
        </h2>
        <p
          style={css(
            'font-size:19px;line-height:1.55;color:#54545E;margin:0 0 40px;max-width:640px'
          )}
        >
          These aren&rsquo;t live yet. Pilot members shape them and get them first as they roll out.
        </p>

        <div
          style={css(
            'display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:22px'
          )}
        >
          {[
            [
              'Item-level benchmarking',
              'See exactly where each of your key items sits versus the market, not just the category picture.',
            ],
            [
              'The dollar impact',
              "Priced at market, here's the revenue you'd have captured. See what a change is worth, not just where you stand.",
            ],
            [
              'Key-item watch',
              "Pick your top-selling items and get alerted when the market moves and you haven't.",
            ],
          ].map(([title, body]) => (
            <div
              key={title}
              style={css(
                'background:#FBFAF7;border:1px dashed #CFCDC5;border-radius:16px;padding:30px 28px;position:relative'
              )}
            >
              <span
                style={css(
                  'position:absolute;top:22px;right:22px;font-size:11px;font-weight:700;letter-spacing:0.05em;text-transform:uppercase;color:#C58A00'
                )}
              >
                Roadmap
              </span>
              <h3
                style={css(
                  "font-family:'Schibsted Grotesk',sans-serif;font-size:20px;font-weight:700;margin:0 34px 12px 0;color:#24276C"
                )}
              >
                {title}
              </h3>
              <p style={css('font-size:16px;line-height:1.55;color:#54545E;margin:0')}>{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Built for how you run */}
      <section style={css('max-width:1180px;margin:0 auto;padding:70px 28px')}>
        <div
          className="site-panel"
          style={css(
            'background:#F6F6F4;border:1px solid #ECEBE6;border-radius:20px;padding:48px 44px'
          )}
        >
          <div style={css('max-width:620px;margin-bottom:34px')}>
            <div
              style={css(
                'font-size:13px;font-weight:600;letter-spacing:0.04em;text-transform:uppercase;color:#E9402B;margin-bottom:14px'
              )}
            >
              Built for how you run
            </div>
            <h2
              style={css(
                "font-family:'Schibsted Grotesk',sans-serif;font-size:clamp(26px,3vw,36px);line-height:1.1;font-weight:800;letter-spacing:-0.02em;margin:0;color:#24276C"
              )}
            >
              Whether you own the P&amp;L or run the floor.
            </h2>
          </div>
          <div
            style={css(
              'display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:22px'
            )}
          >
            {[
              [
                'For the owner / executive',
                'Know the moment something material moves in your market, so pricing decisions are timely and defensible instead of reactive.',
              ],
              [
                'For the operations manager',
                'Keep a finger on the pulse of the local market without pulling your team off the floor to go scout it.',
              ],
            ].map(([title, body]) => (
              <div
                key={title}
                style={css(
                  'background:#fff;border:1px solid #E6E4DE;border-radius:14px;padding:30px 28px'
                )}
              >
                <div
                  style={css(
                    "font-family:'Schibsted Grotesk',sans-serif;font-size:19px;font-weight:700;color:#24276C;margin-bottom:10px"
                  )}
                >
                  {title}
                </div>
                <p style={css('font-size:16px;line-height:1.55;color:#54545E;margin:0')}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pilot form */}
      <section id="pilot" style={css('background:#24276C;color:#fff')}>
        <div
          style={css(
            'max-width:1180px;margin:0 auto;padding:80px 28px;display:grid;grid-template-columns:repeat(auto-fit,minmax(320px,1fr));gap:56px;align-items:start'
          )}
        >
          <div>
            <div
              style={css(
                'font-size:13px;font-weight:600;letter-spacing:0.04em;text-transform:uppercase;color:#F0806F;margin-bottom:14px'
              )}
            >
              Join the pilot
            </div>
            <h2
              style={css(
                "font-family:'Schibsted Grotesk',sans-serif;font-size:clamp(30px,3.6vw,44px);line-height:1.08;font-weight:800;letter-spacing:-0.02em;margin:0 0 20px;color:#fff"
              )}
            >
              Get your menu benchmarked.
            </h2>
            <p
              style={css(
                'font-size:18px;line-height:1.55;color:#B9C0DC;margin:0 0 28px;max-width:460px'
              )}
            >
              Send us your menu and we&rsquo;ll show you how your pricing compares to your real
              local market. Early-access members get first access to everything on the roadmap.
            </p>
            <div style={css('display:flex;flex-direction:column;gap:14px')}>
              {[
                'No cost to join the pilot',
                'Real dine-in data, sourced and traceable',
                'Shape the roadmap that ships next',
              ].map((t) => (
                <div
                  key={t}
                  style={css(
                    'display:flex;gap:12px;align-items:center;font-size:16px;color:#EAECF6'
                  )}
                >
                  <span style={css('color:#F0806F;font-weight:700')}>✓</span>
                  {t}
                </div>
              ))}
            </div>
          </div>

          <form
            onSubmit={onSubmit}
            className="site-form"
            style={css(
              'background:#1A1C50;border:1px solid #35387E;border-radius:18px;padding:30px'
            )}
          >
            <div style={css('display:flex;flex-direction:column;gap:16px')}>
              <div>
                <label style={css(labelStyle)}>Name</label>
                <Field
                  name="name"
                  required
                  placeholder="Your name"
                  style={css(inputStyle)}
                  focusStyle={css(inputFocus)}
                />
              </div>
              <div>
                <label style={css(labelStyle)}>Company</label>
                <Field
                  name="company"
                  required
                  placeholder="Restaurant or group name"
                  style={css(inputStyle)}
                  focusStyle={css(inputFocus)}
                />
              </div>
              <div>
                <label style={css(labelStyle)}>Work email</label>
                <Field
                  type="email"
                  name="email"
                  required
                  placeholder="you@company.com"
                  style={css(inputStyle)}
                  focusStyle={css(inputFocus)}
                />
              </div>
              <div>
                <label style={css(labelStyle)}>
                  Menu URL{' '}
                  <span style={css('color:#7581AB;font-weight:500')}>(or attach after)</span>
                </label>
                <Field
                  name="menuUrl"
                  placeholder="link to your menu"
                  style={css(inputStyle)}
                  focusStyle={css(inputFocus)}
                />
              </div>
              {turnstileEnabled && (
                <Turnstile
                  onVerify={setToken}
                  onExpire={() => setToken('')}
                  resetSignal={resetSignal}
                  theme="dark"
                  size="flexible"
                />
              )}
              <Hover
                as="button"
                type="submit"
                style={css(
                  `margin-top:4px;font-family:inherit;font-size:16px;font-weight:600;color:#fff;background:#E9402B;border:none;border-radius:10px;padding:15px;cursor:pointer${submitting ? ';opacity:0.6' : ''}`
                )}
                hoverStyle={css('background:#D3341F')}
              >
                {submitting ? 'Sending…' : 'Get my benchmark'}
              </Hover>
              {error && (
                <div style={css('font-size:13px;color:#F0806F;text-align:center')}>{error}</div>
              )}
              {submitted && (
                <div style={css('font-size:14px;color:#8FE0A8;text-align:center')}>
                  Thanks. We&rsquo;ll be in touch shortly to get your menu.
                </div>
              )}
              <p
                style={css(
                  'font-size:13px;line-height:1.5;color:#7581AB;margin:2px 0 0;text-align:center'
                )}
              >
                No pricing, no obligation. This is early access, not a purchase.
              </p>
            </div>
          </form>
        </div>
      </section>

      <SiteFooter
        navLinks={[
          { label: 'Home', href: '/' },
          { label: 'What you get now', href: '#now' },
          { label: "Where it's going", href: '#roadmap' },
          { label: 'Join the pilot', href: '#pilot' },
        ]}
      />
    </div>
  );
}
